/**
 * Tenant alerts for the views (phase 7b item 4): the active alerts and their
 * counts (the top bar's badge), the history in pages, the notification
 * settings, the channels and the test send. The data-hook pattern
 * (`rundowns.ts`); live through the change channel (D35: `alert`,
 * `alertSettings` → `keysForChange`).
 *
 * **On a node** the alert routes do not exist (the cloud keeps alerts): the
 * list answers `not-found`, which `alertsQuery` turns into "unavailable here"
 * rather than an error, so the badge simply stays away.
 *
 * **Optimistic concurrency** for the settings form: a save from a version
 * someone else has since replaced comes back as an `AlertSettingsConflict`
 * carrying the settings as they now are (the form offers overwrite or
 * discard, as the studio editor does).
 */
import { ApiError, type ApiClient } from '@acp/api-client/client.js';
import type {
  Alert,
  AlertChannel,
  AlertCounts,
  AlertHistoryEntry,
  AlertSettings,
  CreateAlertChannelRequest,
  TestAlertResult,
  UpdateAlertChannelRequest,
  UpdateAlertSettingsRequest,
} from '@acp/api-contract/alerts.js';
import type { AlertType } from '@acp/tenant-alerts/catalog.js';
import { type QueryClient, infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

export const alertKeys = {
  all: ['alerts'] as const,
  list: (status: 'active' | 'all') => [...alertKeys.all, 'list', status] as const,
  history: (type: AlertType | null) => [...alertKeys.all, 'history', type ?? 'every'] as const,
  settings: () => [...alertKeys.all, 'settings'] as const,
};

/** The alerts a caller may see, or `available: false` where the backend keeps none (a node). */
export type AlertList = { readonly available: true; readonly alerts: readonly Alert[]; readonly counts: AlertCounts } | { readonly available: false };

export const HISTORY_PAGE = 50;

/** The first page's cursor: none. */
const FIRST_PAGE: string | null = null;

export function alertsQuery(api: ApiClient, status: 'active' | 'all') {
  return queryOptions({
    queryKey: alertKeys.list(status),
    queryFn: async (): Promise<AlertList> => {
      try {
        const res = await api.call('listAlerts', { params: null, query: { status }, body: null });
        return { available: true, alerts: res.alerts, counts: res.counts };
      } catch (err) {
        if (err instanceof ApiError && err.code === 'not-found') return { available: false };
        throw err;
      }
    },
  });
}

export type HistoryPage = { readonly entries: readonly AlertHistoryEntry[]; readonly nextCursor: string | null };

export function alertHistoryQuery(api: ApiClient, type: AlertType | null) {
  return infiniteQueryOptions({
    queryKey: alertKeys.history(type),
    initialPageParam: FIRST_PAGE,
    queryFn: async ({ pageParam }): Promise<HistoryPage> =>
      api.call('listAlertHistory', { params: null, query: { limit: HISTORY_PAGE, ...(pageParam === null ? {} : { cursor: pageParam }), ...(type === null ? {} : { type }) }, body: null }),
    getNextPageParam: (last: HistoryPage) => last.nextCursor,
  });
}

export type Choice = { readonly id: string; readonly name: string };

/**
 * The groups and sites the "add channel" dialog offers (tenant administration, like the settings). Under the
 * `groups` and `sites` prefixes, so a change to either (D35) refreshes them.
 */
export function alertPickersQuery(api: ApiClient) {
  return {
    groups: queryOptions({
      queryKey: ['groups', 'alert-picker'] as const,
      queryFn: async (): Promise<readonly Choice[]> => (await api.call('listGroups', { params: null, query: null, body: null })).groups.map((g) => ({ id: g.groupId, name: g.name })),
    }),
    sites: queryOptions({
      queryKey: ['sites', 'alert-picker'] as const,
      queryFn: async (): Promise<readonly Choice[]> => (await api.call('listSites', { params: null, query: null, body: null })).sites.map((s) => ({ id: s.siteId, name: s.name })),
    }),
  };
}

/**
 * Acknowledge an alert for everyone (the operator screen's alert bar): the answer is put straight into the active
 * list, so the bar drops it at once; the change channel (D35) refreshes everyone else's.
 */
export function acknowledgeAlertMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (key: string): Promise<Alert> => (await api.call('acknowledgeAlert', { params: null, query: null, body: { key } })).alert,
    onSuccess: async (alert: Alert): Promise<void> => {
      queryClient.setQueryData(alertKeys.list('active'), (list: AlertList | undefined): AlertList | undefined =>
        list === undefined || !list.available ? list : { ...list, alerts: list.alerts.map((a) => (a.key === alert.key ? alert : a)) },
      );
      await queryClient.invalidateQueries({ queryKey: alertKeys.all });
    },
  };
}

export function alertSettingsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: alertKeys.settings(),
    queryFn: async (): Promise<AlertSettings> => (await api.call('getAlertSettings', { params: null, query: null, body: null })).settings,
  });
}

/** A save refused because the settings moved on: `current` is what they now are. */
export class AlertSettingsConflict extends Error {
  readonly current: AlertSettings;
  constructor(current: AlertSettings) {
    super(`conflict: ${current.updatedBy ?? 'someone'} saved version ${String(current.version)} meanwhile`);
    this.name = 'AlertSettingsConflict';
    this.current = current;
  }
}

export function saveAlertSettingsMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: UpdateAlertSettingsRequest): Promise<AlertSettings> => {
      try {
        return (await api.call('updateAlertSettings', { params: null, query: null, body: input })).settings;
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'conflict') throw err;
        throw new AlertSettingsConflict((await api.call('getAlertSettings', { params: null, query: null, body: null })).settings);
      }
    },
    onSuccess: (settings: AlertSettings): void => {
      queryClient.setQueryData(alertKeys.settings(), settings);
    },
  };
}

export function createAlertChannelMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: CreateAlertChannelRequest): Promise<AlertChannel> => (await api.call('createAlertChannel', { params: null, query: null, body: input })).channel,
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: alertKeys.settings() });
    },
  };
}

export function updateAlertChannelMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly channelId: string; readonly change: UpdateAlertChannelRequest }): Promise<AlertChannel> =>
      (await api.call('updateAlertChannel', { params: { channelId: input.channelId }, query: null, body: input.change })).channel,
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: alertKeys.settings() });
    },
  };
}

export function deleteAlertChannelMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (channelId: string): Promise<AlertChannel> => (await api.call('deleteAlertChannel', { params: { channelId }, query: null, body: null })).channel,
    onSuccess: async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: alertKeys.settings() });
    },
  };
}

export function testAlertMutation(api: ApiClient) {
  return {
    mutationFn: async (channelId: string | null): Promise<readonly TestAlertResult[]> => (await api.call('testAlert', { params: null, query: null, body: { channelId } })).results,
  };
}
